import { Card } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import {
    useRecurringCharges,
    useRentRoll,
    useSelectedDataLevel,
} from 'waypoint-hooks';
import { DisabledDashboard } from 'waypoint-react';
import TopTenantsTable from './TopTenantsTable';
import { safeDivision } from 'waypoint-utils';
import TopTenantsChart from './TopTenantsChart';
import {
    filterRecurringCharges,
    isRecurringChargeLease,
    isUnitOccupied,
} from 'components/leases/components/recurring-charge/utils';
import { RecurringCharge, RentRollProps } from 'waypoint-types';
import { DataGrid } from 'devextreme-react';
import { TenantsDict, metricOptions, topNOptions } from './topTenantsUtils';
import { numericalSort } from 'utils/tables/sorters';
import { css } from 'emotion';
import TopTenantsCardHeader from './TopTenantsCardHeader';
import { sanitize } from 'dompurify';
import { AppFeaturePermissions } from 'contexts';
import { PermissionedWrapper } from 'components/permissionGroups/PermissionedWrapper';

const containerStyle = (isPDFExport?: boolean) => css`
    display: flex;
    align-items: flex-end;
    flex-wrap: ${isPDFExport ? 'wrap' : 'nowrap'};
    @media screen and (max-width: 1268px) {
        flex-wrap: wrap;
    }
`;

interface TopTenantsCardProps {
    entityCode: string;
    widgetMetric?: string;
    widgetTopNTenants?: string;
    widgetSelectedChargeCode?: string[];
    widgetVisualOption?: string;
    isPDFExport?: boolean;
    hideSelectorsFromTitle?: boolean;
    dataGridRef?: React.RefObject<DataGrid<any, any>>;
    setIsChartReadyToExport?: (isChartReadyToExport: boolean) => void;
    chartExportId?: string;
    gridExportId?: string;
    exportWidth?: string;
    narrativePosition?: string;
    narrativeText?: string;
    setIsLoading?: (isLoading: boolean) => void;
}

const TopTenantsCard = ({
    entityCode,
    widgetMetric,
    widgetSelectedChargeCode,
    widgetTopNTenants,
    widgetVisualOption,
    hideSelectorsFromTitle,
    dataGridRef,
    isPDFExport,
    chartExportId,
    gridExportId,
    setIsChartReadyToExport,
    exportWidth,
    narrativePosition,
    narrativeText,
    setIsLoading,
}: TopTenantsCardProps) => {
    const [selectedChargeCode, setSelectedChargeCode] = useState<string[]>(
        widgetSelectedChargeCode ?? []
    );
    const [selectedMetric, setSelectedMetric] = useState<string>(
        widgetMetric ?? metricOptions[0].value
    );
    const [topNTenants, setTopNTenants] = useState<string>(
        widgetTopNTenants ?? topNOptions[2].value
    );

    const selectedDataLevel = useSelectedDataLevel();

    const { data: rentRoll, isLoading: isRentRollLoading } = useRentRoll(
        [entityCode],
        selectedDataLevel
    );

    useEffect(() => {
        if (selectedMetric === metricOptions[0].value) {
            setSelectedChargeCode([]);
        }
    }, [selectedMetric]);

    setIsLoading && setIsLoading(isRentRollLoading);

    const { data } = useRecurringCharges([entityCode], selectedDataLevel);

    const onChange = useCallback(
        (value: string[]) => {
            setSelectedChargeCode(value);
        },
        [setSelectedChargeCode]
    );

    if (!rentRoll || !data) {
        return <DisabledDashboard text={'Loading'} />;
    }
    const { recurringCharges, rollUp } = data;

    const filteredCharges = filterRecurringCharges(
        recurringCharges,
        selectedChargeCode
    ) as RecurringCharge[];

    const calculateTotalMonthlyCharge = (rentRoll: RentRollProps) => {
        return filteredCharges
            .filter((rc) => isRecurringChargeLease(rc, rentRoll))
            .reduce((acc: number, curr: RecurringCharge) => {
                return acc + curr.monthly;
            }, 0);
    };

    const rentRollWithTotalCharges = () => {
        const rollUpAnnualTotal = rollUp.reduce(
            (acc, curr) => acc + curr.annual,
            0
        );

        return rentRoll.map((rr) => {
            const totalMonthly = calculateTotalMonthlyCharge(rr);
            return {
                ...rr,
                total_monthly: isUnitOccupied(rr) ? totalMonthly : null,
                total_annual: isUnitOccupied(rr) ? totalMonthly * 12 : null,
                total_annual_percentage: isUnitOccupied(rr)
                    ? safeDivision(totalMonthly * 12, rollUpAnnualTotal)
                    : null,
                total_monthly_per_sq_ft: isUnitOccupied(rr)
                    ? safeDivision(totalMonthly, rr.rentable_sq_ft)
                    : null,
                total_annual_per_sq_ft: isUnitOccupied(rr)
                    ? safeDivision(totalMonthly * 12, rr.rentable_sq_ft)
                    : null,
            };
        });
    };

    const calculateTableData = () => {
        const sqFtCalculations = rentRollWithTotalCharges().reduce(
            (acc, r) => {
                if (r.space_occupancy_status === 'OCCUPIED') {
                    acc.occupiedRentableSqFt =
                        (acc.occupiedRentableSqFt ?? 0) +
                        (r.rentable_sq_ft ?? 0);
                }
                acc.totalRentableSqFt =
                    (acc.totalRentableSqFt ?? 0) + (r.rentable_sq_ft ?? 0);
                return acc;
            },
            { occupiedRentableSqFt: 0, totalRentableSqFt: 0 }
        );

        const rentRollByTenant = rentRollWithTotalCharges().reduce(
            (dict, r) => {
                if (r.tenant_name) {
                    if (!Object.keys(dict).includes(r.tenant_name)) {
                        dict[r.tenant_name] = {
                            tenant: r.tenant_name,
                            suites:
                                r.space_number?.length > 0
                                    ? [
                                          `${r.space_number}_${r.entity_display_code}`,
                                      ]
                                    : [],
                            rentable_sq_ft: r.rentable_sq_ft,
                            percentage_occ_sq_ft: safeDivision(
                                r.rentable_sq_ft,
                                sqFtCalculations.occupiedRentableSqFt
                            ),
                            percentage_rentable_sq_ft: safeDivision(
                                r.rentable_sq_ft,
                                sqFtCalculations.totalRentableSqFt
                            ),
                            gross_rent: r.in_place_base_rent,
                            total_annual: r.total_annual ?? 0,
                            total_monthly: r.total_monthly,
                            total_monthly_per_sq_ft: r.total_monthly_per_sq_ft,
                            total_annual_percentage:
                                r.total_annual_percentage ?? 0,
                            total_annual_per_sq_ft:
                                (r.total_annual_per_sq_ft ?? 0) *
                                (r.rentable_sq_ft ?? 0),
                            rent_per_sq_ft: r.in_place_base_rent_per_sq_ft,
                            industry:
                                r.tenant_industry.length > 0
                                    ? [r.tenant_industry]
                                    : [],
                            total_weight: r.rentable_sq_ft,
                        };
                    } else {
                        dict[r.tenant_name].rentable_sq_ft +=
                            r.rentable_sq_ft ?? 0;
                        dict[r.tenant_name].gross_rent += r.in_place_base_rent;
                        dict[r.tenant_name].total_annual += r.total_annual ?? 0;
                        dict[r.tenant_name].total_annual_percentage +=
                            r.total_annual_percentage ?? 0;
                        dict[r.tenant_name].total_annual_per_sq_ft +=
                            (r.total_annual_per_sq_ft ?? 0) *
                            (r.rentable_sq_ft ?? 0);
                        dict[r.tenant_name].total_weight += r.rentable_sq_ft;
                        dict[r.tenant_name].percentage_occ_sq_ft = safeDivision(
                            dict[r.tenant_name].rentable_sq_ft ?? 0,
                            sqFtCalculations.occupiedRentableSqFt
                        );
                        dict[r.tenant_name].percentage_rentable_sq_ft =
                            safeDivision(
                                dict[r.tenant_name].rentable_sq_ft ?? 0,
                                sqFtCalculations.totalRentableSqFt
                            );
                        if (
                            r.tenant_industry.length > 0 &&
                            !dict[r.tenant_name].industry.includes(
                                r.tenant_industry
                            )
                        ) {
                            dict[r.tenant_name].industry.push(
                                r.tenant_industry
                            );
                        }
                        if (
                            r.space_number.length > 0 &&
                            !dict[r.tenant_name].suites.includes(
                                `${r.space_number}_${r.entity_display_code}`
                            )
                        ) {
                            dict[r.tenant_name].suites.push(
                                `${r.space_number}_${r.entity_display_code}`
                            );
                        }
                    }
                }
                return dict;
            },
            {} as TenantsDict
        );

        // Calculate the weighted average
        Object.values(rentRollByTenant).forEach((tenant) => {
            tenant.total_annual_per_sq_ft = safeDivision(
                tenant.total_annual_per_sq_ft,
                tenant.total_weight
            );
        });

        return { rentRollByTenant, sqFtCalculations };
    };

    const tenantsTableData = calculateTableData();

    const sortedData = (tenantsTableData: TenantsDict) => {
        if (!tenantsTableData) {
            return [];
        }

        return Object.values(tenantsTableData).sort((a, b) => {
            if (selectedMetric === 'rentable_sf') {
                return numericalSort(b.rentable_sq_ft, a.rentable_sq_ft);
            }

            if (selectedMetric === 'annual_charges') {
                return numericalSort(b.total_annual ?? 0, a.total_annual ?? 0);
            }

            if (selectedMetric === 'annual_charges_per_sq_ft') {
                return numericalSort(
                    b.total_annual_per_sq_ft,
                    a.total_annual_per_sq_ft
                );
            }

            return 0;
        });
    };

    const allTenantsOrPartialTenants =
        topNTenants === 'all'
            ? sortedData(tenantsTableData.rentRollByTenant)
            : sortedData(tenantsTableData.rentRollByTenant).slice(
                  0,
                  parseInt(topNTenants)
              );

    const narrativeTextBox = () => {
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitize(narrativeText ?? ''),
                }}
            />
        );
    };
    return (
        <PermissionedWrapper featureKey={AppFeaturePermissions.TopTenants}>
            <Card
                data-test="top-tenants-card"
                bordered={!isPDFExport}
                title={
                    hideSelectorsFromTitle ? null : (
                        <TopTenantsCardHeader
                            selectedMetric={selectedMetric}
                            setSelectedMetric={setSelectedMetric}
                            topNTenants={topNTenants}
                            setTopNTenants={setTopNTenants}
                            isRentRollLoading={isRentRollLoading}
                            onChange={onChange}
                            recurringCharges={recurringCharges}
                            selectedChargeCode={selectedChargeCode}
                        />
                    )
                }
            >
                <div className={containerStyle(isPDFExport)}>
                    <div
                        id={chartExportId}
                        data-test="top-tenants-chart-container"
                        style={{ width: '100%' }}
                    >
                        {narrativePosition === 'above'
                            ? narrativeTextBox()
                            : null}
                        <TopTenantsChart
                            tenants={allTenantsOrPartialTenants}
                            setIsChartReadyToExport={setIsChartReadyToExport}
                            isPDFExport={isPDFExport}
                            exportWidth={exportWidth}
                            chartExportId={chartExportId}
                            metric={selectedMetric}
                            widgetVisualOption={widgetVisualOption}
                        />
                    </div>

                    <div
                        id={gridExportId}
                        data-test="top-tenants-table-container"
                        style={{ overflow: 'auto' }}
                    >
                        <TopTenantsTable
                            data={allTenantsOrPartialTenants}
                            sqFtCalculations={tenantsTableData.sqFtCalculations}
                            dataGridRef={dataGridRef}
                            isPDFExport={isPDFExport}
                        />
                    </div>
                </div>
            </Card>
        </PermissionedWrapper>
    );
};

export default TopTenantsCard;
